import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

/**
 * Environment Detection Service
 * Detects the current environment based on hostname at runtime
 * Useful for determining which API to call when deployed to different URLs
 */
@Injectable({
  providedIn: 'root'
})
export class EnvironmentService {
  private detectedEnvironment: string = 'development';

  constructor() {
    this.detectEnvironment();
  }

  /**
   * Detect environment from hostname at runtime
   * This allows a single build to work across multiple deployment URLs
   */
  private detectEnvironment(): void {
    if (typeof window !== 'undefined' && window.location?.hostname) {
      const hostname = window.location.hostname.toLowerCase();
      // Match hostname to environment
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        // On local development, honor the build-time configuration.
        // This enables testing qc/prod/demo/uat locally via --configuration.
        this.detectedEnvironment = environment.name;
      } else if (hostname.includes('qc') || hostname.includes('msuitedocsilaqc')) {
        this.detectedEnvironment = 'qc';
      } else if (hostname.includes('prod') || hostname.includes('msuitedocsila.z29')) {
        this.detectedEnvironment = 'production';
      } else if (hostname.includes('demo') || hostname.includes('msuitedocsilademo')) {
        this.detectedEnvironment = 'demo';
      } else if (hostname.includes('uat') || hostname.includes('msuitedocsilauat')) {
        this.detectedEnvironment = 'uat';
      } else {
        // Default to build-time environment
        this.detectedEnvironment = environment.name;
      }
      // Store in window for easy access in console or debugging
      (window as any).__APP_ENVIRONMENT__ = {
        detected: this.detectedEnvironment,
        buildTime: environment.name,
        hostname: hostname,
        apiBaseUrl: environment.apiBaseUrl
      };
    }
  }

  /**
   * Get the detected runtime environment
   */
  getEnvironment(): string {
    return this.detectedEnvironment;
  }

  /**
   * Get environment info for debugging
   */
  getEnvironmentInfo(): {
    detected: string;
    buildTime: string;
    hostname: string;
    apiBaseUrl: string;
    b2cTenant: string;
  } {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : 'unknown';
    return {
      detected: this.detectedEnvironment,
      buildTime: environment.name,
      hostname: hostname,
      apiBaseUrl: environment.apiBaseUrl,
      b2cTenant: environment.b2c.tenant
    };
  }

  /**
   * Check if running in specific environment
   */
  isEnvironment(env: 'development' | 'local' | 'qc' | 'production' | 'demo' | 'uat'): boolean {
    return this.detectedEnvironment === env;
  }

  /**
   * Check if running in production mode
   */
  isProduction(): boolean {
    return environment.production;
  }

  /**
   * Log environment info to console (useful for debugging)
   */
  logEnvironmentInfo(): void {
    const info = this.getEnvironmentInfo();
  }
}
